import { ButtonHTMLAttributes, forwardRef, memo } from 'react'
import { cn } from '@/lib/utils'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline' | 'gradient' | 'soft'
  size?: 'xs' | 'sm' | 'md' | 'lg'
  loading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  fullWidth?: boolean
  pill?: boolean
}

const Button = memo(forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant = 'primary', 
    size = 'md', 
    loading, 
    children, 
    disabled, 
    leftIcon,
    rightIcon,
    fullWidth = false,
    pill = false,
    type = 'button',
    ...props 
  }, ref) => {
    const isDisabled = disabled || loading

    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          // Modern base styles - sophisticated & interactive
          'inline-flex items-center justify-center gap-2 font-medium transition-all duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed',
          'relative overflow-hidden',
          'select-none',
          
          // Size variations with better proportions
          {
            'h-7 px-2.5 text-xs': size === 'xs',
            'h-9 px-3.5 text-sm': size === 'sm',
            'h-10 px-4 text-sm': size === 'md', 
            'h-11 px-6 text-base': size === 'lg',
          },
          
          // Modern variant styles - clean & professional
          {
            // Primary - Solid with Exhibit C blue
            'bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white shadow-sm hover:shadow': variant === 'primary',
            
            // Secondary - Modern bordered
            'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-2 border-gray-300 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-750': variant === 'secondary',
            
            // Ghost - Minimal hover
            'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100': variant === 'ghost',
            
            // Outline - Clean border
            'bg-transparent border-2 border-primary-600 dark:border-primary-500 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-950/20': variant === 'outline',
            
            // Gradient - Modern gradient for CTAs
            'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-md hover:shadow-lg transform hover:scale-105 active:scale-100': variant === 'gradient',
            
            // Soft - Subtle background
            'bg-primary-50 dark:bg-primary-950/30 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/40 border border-primary-200 dark:border-primary-800': variant === 'soft',
          },
          
          // Shape variations
          {
            'rounded-lg': !pill,
            'rounded-full': pill,
          },
          
          // Full width
          {
            'w-full': fullWidth,
          },
          
          className
        )}
        disabled={isDisabled}
        {...props}
      >
        {/* Loading Spinner - minimal */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        )}

        {/* Button Content */}
        <span className={cn('flex items-center gap-2 relative z-10', { 'opacity-0': loading })}>
          {leftIcon && (
            <span className="flex-shrink-0 transition-transform group-hover:scale-110" aria-hidden="true">
              {leftIcon}
            </span>
          )}
          
          {children && (
            <span className="font-medium">
              {children}
            </span>
          )}
          
          {rightIcon && (
            <span className="flex-shrink-0 transition-transform group-hover:translate-x-0.5" aria-hidden="true">
              {rightIcon}
            </span>
          )}
        </span>
        
        {/* Subtle hover effect overlay */}
        {variant === 'gradient' && (
          <span className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200" aria-hidden="true" />
        )}
      </button>
    )
  }
))

Button.displayName = 'Button'

export default Button 